/**
 * Owner: Masa Bayu
 * File: page.tsx
 * Date:Time: 2026-10-05 12:55
 * Description: Live PoC · Page 19/65 · Fasa 12 fonts · Cinzel + Glamy + Calibri.
 *
 * Note: mounted by RoutePanel at /p19-hijrah.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { QxEditorPageFaceSpsu } from '../../profile/qx-editor-page-face';
import type { AssetLibrary, AssetReference, SPSUDocument, SPSUPage } from '../../profile/spsu-types';
import '../p02/p02-live.css';

const SPSU_JSON = '/evidence/pkt-ladder/spsu-p19-hijrah-poc.json';
const ASSETS_MANIFEST = '/evidence/pkt-ladder/spsu-p19-assets/manifest.json';

type EvidenceFile = {
  readonly spsu: SPSUDocument;
  readonly hijrahElement?: {
    readonly id: string;
    readonly content: string;
  } | null;
  readonly VERDICT?: string;
  readonly fasa12?: {
    readonly babTiga?: {
      readonly fontFamily: string;
      readonly fontWeight: number;
    } | null;
  };
};

async function loadJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
  return (await response.json()) as T;
}

/** Alias Fasa 12 clean family names onto measured font files. */
function alignFasa12FontAliases(assets: AssetLibrary): AssetLibrary {
  const next: Record<string, AssetReference> = { ...assets };
  const patch = (
    key: string,
    familyName: string,
    extraAliases: readonly string[],
    weight?: string,
  ): void => {
    const asset = next[key];
    if (!asset?.font) return;
    const aliases = new Set(asset.font.aliases ?? []);
    aliases.add(familyName);
    for (const a of extraAliases) aliases.add(a);
    next[key] = {
      ...asset,
      font: {
        ...asset.font,
        familyName,
        aliases: [...aliases],
        ...(weight ? { weight } : {}),
      },
    };
  };
  patch('font_cinzeldecorative_black', 'CinzelDecorative', ['CinzelDecorative-Black', 'Cinzel'], '900');
  patch('font_glamy_sunrise_regular', 'GlamySunrise', ['GlamySunrise-Regular'], '400');
  patch('font_aptos_serif_bold', 'Aptos Serif', ['AptosSerif'], '700');
  patch('font_calibri', 'Calibri', [], '400');
  patch('font_baskerville_normal', 'Baskerville', ['Baskerville-Normal'], '400');
  return next;
}

/**
 * Live Face · Page 19 (footer 65) · Fasa 12 font fidelity + paragraph justify.
 */
export default function P19HijrahGlueLivePage(): ReactElement {
  const [page, setPage] = useState<SPSUPage | null>(null);
  const [assets, setAssets] = useState<AssetLibrary>({});
  const [hijrah, setHijrah] = useState<EvidenceFile['hijrahElement']>(null);
  const [verdict, setVerdict] = useState<string>('');
  const [babMeta, setBabMeta] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [evidence, manifest] = await Promise.all([
          loadJson<EvidenceFile>(SPSU_JSON),
          loadJson<AssetLibrary>(ASSETS_MANIFEST),
        ]);
        const next = evidence.spsu.pages[0] ?? null;
        if (!next) throw new Error('SPSU has no page');
        if (cancelled) return;
        setPage(next);
        setAssets(alignFasa12FontAliases(manifest));
        setHijrah(evidence.hijrahElement ?? null);
        setVerdict(evidence.VERDICT ?? '');
        const bab = evidence.fasa12?.babTiga;
        setBabMeta(
          bab
            ? `${bab.fontFamily} · weight ${bab.fontWeight}`
            : 'BAB TIGA meta missing',
        );
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="p02-live p02-live-error" role="alert">
        ERROR: {error}
      </div>
    );
  }
  if (!page) {
    return <div className="p02-live p02-live-loading">Loading SPSU P19 Hijrah…</div>;
  }

  return (
    <div className="p02-live" data-p19-hijrah-poc="" data-fasa12-font="">
      <header className="p02-live-header">
        <p className="p02-live-kicker">p19-hijrah · Fasa 12</p>
        <h1>Page 65 Face · Font Fidelity + Justify</h1>
        <p>
          {verdict} · BAB TIGA → <code>{babMeta}</code>
        </p>
        <p>
          <code>{hijrah?.content ?? '—'}</code>
        </p>
      </header>
      <div className="p02-live-grid">
        <section className="p02-live-pane">
          <h2>Live Face · Page 19 / footer 65</h2>
          <div className="p02-live-face-host">
            <QxEditorPageFaceSpsu page={page} assets={assets} editable={false} fit />
          </div>
        </section>
      </div>
    </div>
  );
}
